import { useQuery } from "react-query";
import { Driver } from "../models";
import { IDriver } from "../types";
import { useCache } from "../context/CacheContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { useUserSession } from "../context/UserContext";
import { listDrivers } from "../data/api/drivers";
import { queryKeys } from "../data/queryKeys";

/**
 * Workspace drivers, read through the REST API (previously a direct Firestore
 * query). Query key, cache-key invalidation and `Driver[]` instance return
 * shape are preserved.
 *
 * `subscribe` used to enable a Firestore real-time listener (e.g. the driver
 * map). The REST API has no live channel, so we approximate it with periodic
 * background polling, keeping the data fresh without a socket.
 */
const useDrivers = (workspaceId?: string, subscribe = false) => {
  const { cacheKey } = useCache();
  const { activeWorkspace } = useWorkspace();
  const { userSession, isAuthenticated } = useUserSession();
  const resolvedWorkspaceId = workspaceId ?? activeWorkspace?.workspaceId ?? "";
  const token = userSession?.token?.accessToken;

  return useQuery<Driver[], Error>(
    queryKeys.drivers(cacheKey, resolvedWorkspaceId),
    async () => {
      const drivers = await listDrivers(token as string, resolvedWorkspaceId);
      return drivers.map((driver: IDriver) => new Driver(driver));
    },
    {
      enabled: !!token && !!resolvedWorkspaceId && isAuthenticated(),
      keepPreviousData: true,
      refetchInterval: subscribe ? 15000 : false,
    },
  );
};

export default useDrivers;
